// TODO: this "component" still needs love
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import clsx from 'clsx';

import {i18n} from '@netx/core/i18n';

import {Typography} from '@mui/material';
import {createStyles, withStyles, type WithStyles} from '@mui/styles';
import type {Theme} from '@netx/core-components/Theme';
import {messages} from '../../properties';
import {useGridStore, useProgressStore, useSelectionStore} from '../../store';
import {
	getInputFormObject,
	isEmptyValue,
	withForm,
	LegacyForm as Form,
	FormType,
	WithFormPropsIn,
	WithFormPropsOut,
} from '@netx/core-components/Form';
import SubmitButton from '@netx/core-components/SubmitButton';

import {LegacyFormAttribute} from '@netx/core-attribute/components/Attribute';
import {
	useAttributeProvider,
	ATTRIBUTE_APPENDABLE_TYPES,
} from '@netx/core-attribute';

import AppendAction from '@netx/core-attribute-quick-edit/components/Actions/AppendToggle';

const styles = (theme: Theme) =>
	createStyles({
		root: {
			background:
				theme.palette.mode === 'light'
					? 'rgba(0,0,0,0.02)'
					: 'rgba(255,255,255,0.02)',

			flexDirection: 'column',
			height: 108,
			padding: theme.spacing(1, 2),
			position: 'relative',

			'&, & $message': {
				alignItems: 'center',
				display: 'flex',
				justifyContent: 'center',
			},
			'&, & $action, & $submit': {
				flex: '0 0 auto',
			},
			'& textarea': {
				maxHeight: 42,
				...theme.mixins.forceScrollable,
			},
			'& $form, & $inputWrapper': {
				alignItems: 'center',
				display: 'flex',
				flex: '1 1 auto',
				minHeight: 0,
				minWidth: 0,
			},
			'& $form, & $message': {
				width: '100%',
			},
			'& $actions, & $inputWrapper': {
				paddingRight: theme.spacing(1),
			},
		},
		actions: {},
		form: {},
		inputWrapper: {
			height: '100%',
			maxHeight: '100%',
		},
		loading: {
			pointerEvents: 'none',
		},
		message: {
			color: theme.palette.text.secondary,
			flex: '0 1 auto',
		},
		submit: {
			'& > button': {
				minWidth: 115,
			},
		},
	});

// TODO: make this component accept the Values and pass them to the form
type FormValues = Record<string, any>;

export interface InputLineProps extends WithFormPropsIn<{}, FormValues> {
	onSubmit(
		values: FormValues,
		changes: FormType['changes'],
		actions: FormType['actions'],
	): Promise<any>;
}

const InputLine = ({
	classes,
	form,
	onSubmit,
}: WithStyles<typeof styles> &
	WithFormPropsOut<InputLineProps, FormValues>) => {
	const [appending, setAppending] = useState(false);
	const [canAppend, setCanAppend] = useState(false);
	const attributeProvider = useAttributeProvider();

	const selection = useSelectionStore();
	const grid = useGridStore();
	const progress = useProgressStore();

	const selectedField = selection.state.inSingleColumn;
	const canSubmit =
		form.hasChanged &&
		form.isValid &&
		!progress.state.working &&
		(!appending ||
			(selectedField !== false && !isEmptyValue(form.values[selectedField])));
	const multipleFieldsSelected =
		selectedField === false && !!selection.state.count;
	const working = progress.state.working || form.isSubmitting;

	const inputForm = getInputFormObject(form);

	// When the selection changes, and the selection is in a single column,
	// get the attribute data for that field
	const attrData = useMemo(() => {
		if (selectedField === false) return null;
		const attr = attributeProvider.getByName(selectedField);
		return attr && !attr.system ? attr : null;
	}, [selectedField, attributeProvider.getByName]);

	// When selected data updates, and the selection is in a single column,
	// update the form to reflect updated data
	useEffect(() => {
		const {storedData} = grid.state;
		if (selectedField === false || !storedData) return;

		const keys = Object.keys(storedData);
		const fieldLength = keys.length;
		if (!fieldLength || fieldLength > 1) {
			form.reset();
			return;
		}

		const fieldName = keys[0];
		const data = storedData[fieldName];
		form.reset({[fieldName]: data.value}, form.actionsRef.current[fieldName], {
			[fieldName]: data.multipleValues === true,
		});
	}, [grid.state.storedData, selectedField]);

	// When attribute data updates, check to see if the type is appendable
	useEffect(() => {
		setCanAppend(
			attrData
				? ATTRIBUTE_APPENDABLE_TYPES.includes(attrData.type) &&
						attrData.permission !== 'r' &&
						grid.state.storedData?.[attrData.name]?.readOnly !== true
				: false,
		);
	}, [attrData, grid.state.storedData]);

	// If the attribute is of a type that can no longer append, and the selection
	// is in a single column, turn append off and update form action
	useEffect(() => {
		if (selectedField === false || canAppend) return;

		setAppending(false);
	}, [canAppend, selectedField]);

	const handleSubmit = useCallback(
		async (values: any, changes: any, actions: any) => {
			if (appending) {
				Object.keys(actions).forEach((field) => (actions[field] = 'append'));
			}
			onSubmit?.(values, changes, actions);
		},
		[appending, onSubmit],
	);

	// We do not need to render for all the context changes we are utilizing
	return useMemo(
		() => (
			<div
				className={clsx(classes.root, {
					[classes.loading]: working,
				})}
			>
				{selectedField !== false && attrData != null ? (
					<Form form={form} className={classes.form} onSubmit={handleSubmit}>
						<div className={classes.inputWrapper}>
							<LegacyFormAttribute
								// TODO: get this in line with other attribute forms
								action={form.actions[selectedField] as any}
								alignActions="center"
								attrData={attrData}
								name={selectedField}
								clearable
								enforceMandatory={false}
								form={inputForm}
								limitHeightToContainer
								multipleValues={form.multipleValues[selectedField]}
								originalValue={form.originalValues[selectedField]}
								readOnly={grid.state.storedData?.[selectedField]?.readOnly}
								value={form.values[selectedField]}
							/>
						</div>
						<div className={classes.actions}>
							<AppendAction
								disabled={!canAppend}
								on={appending}
								onToggle={(event, on) => setAppending(on)}
							/>
						</div>
						<div className={classes.submit}>
							<SubmitButton
								disabled={!canSubmit}
								label={appending ? i18n.t('i18n.append') : i18n.t('i18n.apply')}
							/>
						</div>
					</Form>
				) : (
					<>
						<div className={classes.message}>
							<Typography variant="body1">
								{multipleFieldsSelected
									? messages.singleFieldMultipleFieldsSelected
									: messages.singleFieldNoFieldsSelected}
							</Typography>
						</div>
					</>
				)}
			</div>
		),
		[
			classes,
			form.actions,
			form.multipleValues,
			form.originalValues,
			form.values,

			inputForm,

			appending,
			attrData,
			canAppend,
			canSubmit,
			multipleFieldsSelected,
			selectedField,
			working,
		],
	);
};

export default withStyles(styles)(
	// @ts-ignore
	withForm<InputLineProps, FormValues>(InputLine, {
		resetOnSubmit: false,
	}),
);
